// Cloud tops from orbit: what lies under one texel of the cloud map.
//
// The cloud map is real imagery (NASA's Blue Marble clouds, rebuilt from the
// 1 km original), 4.9 km a texel in the 8K and 1 km in the streamed pages
// (rocketry/cloudimagery.js). From 200 km up a pixel spans 180 m, so the
// globe and rocketry's orbital shell draw the map magnified. Above the
// texel the map is the truth: where the clouds are, how the cells and
// streets are arranged, where the sea is clear. Under it this chunk supplies
// what the data cannot resolve, and nothing else.
//
// The structure under the texel is a fixed latent field, a sum of three
// bands a factor of three apart in scale (cells of 3 km, 1 km and 330 m,
// the 3 km band carrying almost all the variance and the finer two the
// rims: CT_AMP). Each band is the orbit volume
// (cloudnoise.js ORBIT_FRAG) read on the sphere in metres: a Perlin-Worley
// Gaussianised at bake, so every sum of bands is a unit normal and a
// threshold on it has a closed-form mean. The map gives the fraction f of
// the ground under cloud; the threshold is set so the expected cover is f.
// The band set is the same everywhere and at all times: a page arriving
// sharpens the map value the field is cut at, and the cells it is cut
// from stay as they were. A field coarse against the page's 1 km texel is
// what makes a thresholded cumulus compact (one cloud per bump in the data,
// its size set by the field); one fine against it scattered each bump into
// grain.
//
// That is what lets the view filter the clouds without moving them. The
// threshold t depends on the place alone. The pixel's footprint only
// decides how much of each band it resolves: a band whose cells span under
// one and a half pixels is not read at all (it could only print noise; it
// fades in up to three), a band read at
// the mip the footprint picks keeps a measured share of its variance, and
// the rest, with the resolved field's own spread across the pixel, is
// treated as unresolved, which widens the carve's edge by exactly that much
// (the prefiltered threshold of Heitz et al. 2013, "Filtering color mapped
// textures and surfaces"). The mean stays f at every footprint, and once
// nothing is resolved the cover is the map's own fraction to the last bit,
// so the far view needs no other path and there is no hand-off band to
// sweep across the disc.
//
// The map says more than the fraction. Its local high-pass (four taps round
// the point against a coarse mip) predicts how broken the cloud under the
// texel really is (r about 0.7 against the 1 km original): a smooth texel is
// a translucent film, a textured one discrete cells with crisp rims. Its
// regional value (twenty kilometres) says whether this is scattered cumulus
// on clear sea or a deck, and its brightness how much of the cloud is thick
// core. All of those are read on the 8K, so they too are the same whether a
// page is resident or not.
//
// The parts, each a function below:
//   cloudMapPoint / cloudMapSplit  what the map says at a point
//   cloudTopField                  the carve: cover, opacity, core, height
//   cloudTopColour                 the cloud's own colour, lit and veiled
//   cloudMassShade                 the face of a mass away from the sun
// The ground shadow is cloudPagedGroundShadow (rocketry/cloudimagery.js),
// which probes this carve along the sun through the paged map.
//
// Positions are unit normals in the map's own drifted address
// (earthweather.js: the reference is advected in latitude-dependent winds and
// deformed on the sphere), so the carved cells travel with the formations
// they belong to. The tangent frame is built from that normal the way the
// globe builds its own, and the drift is a rotation about the spin axis, so
// the sun's tangent components computed in the real frame hold in this one.

import { ORBIT_GRAD_RANGE } from '../rocketry/cloudnoise.js';

// A shoulder under the bloom pass's threshold (0.92 in luminance, main.js):
// the brightest tops still read white after the tone curve, and a sunlit
// deck does not glow a halo onto the sea round it. The volumetric clouds
// take the same one far off (cloudshaders.js), so where the two hand over
// they are as bright as each other.
export const CLOUD_SHOULDER_GLSL = /* glsl */`
vec3 cloudLumaShoulder(vec3 col) {
  float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
  if (l > 0.84) col *= (0.84 + 0.075 * (1.0 - exp((0.84 - l) / 0.075))) / l;
  return col;
}
`;

export const EARTH_CLOUD_TOP_GLSL = /* glsl */`
precision highp sampler3D;
${CLOUD_SHOULDER_GLSL}
// Cloud over the ground it covers, as light adds. A pixel part covered by
// cloud, or seen through a translucent one, receives the two radiances in
// proportion; an OKLab mix at weight a lands on the cube-root midpoint
// instead, so a half covered pixel over dark sea read at a quarter of the
// light, and it kept (1 - a) of the sea's chroma where the sea's share of
// the light is far smaller: thin cloud over the sea went dark and blue
// (EPIC's thin cloud over the sea is a neutral grey; the render's was a
// quarter darker and plainly blue). This stays a straight line across OKLab,
// the near-grey branch of oklchMixNeutral (no hue arc, so no lilac), with
// the lightness at the weight that carries the linear-light luminance and
// the chroma at the cloud's share of that light. It also makes the carve's
// mean opacity its mean light, so the far view and the near one agree. The
// globe and the orbital shell call it alike (earth.js, orbitclouds.js).
float cloudCompositeWeight(vec3 ground, vec3 cloud, float a) {
  const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
  float yg = max(dot(ground, LUMA), 1e-6), yc = max(dot(cloud, LUMA), 1e-6);
  float lg = pow(yg, 0.33333333), lc = pow(yc, 0.33333333);
  float d = lc - lg;
  if (abs(d) < 1e-4) return a;
  return clamp((pow(mix(yg, yc, clamp(a, 0.0, 1.0)), 0.33333333) - lg) / d, 0.0, 1.0);
}
vec3 cloudComposite(vec3 ground, vec3 cloud, float a) {
  const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
  a = clamp(a, 0.0, 1.0);
  vec3 lg = _linSrgbToOklab(max(ground, 0.0)), lc = _linSrgbToOklab(max(cloud, 0.0));
  float yg = max(dot(ground, LUMA), 1e-6), yc = max(dot(cloud, LUMA), 1e-6);
  float wC = a * yc / mix(yg, yc, a);
  return _oklabToLinSrgb(vec3(mix(lg.x, lc.x, cloudCompositeWeight(ground, cloud, a)), mix(lg.yz, lc.yz, wC)));
}
uniform sampler3D uCloudOrbitVol;    // cloudnoise.js orbit: R a Gaussianised band, GBA its gradient
uniform float uCloudVolOn;           // 1 once the volume is bound
uniform float uCloudTopQuality;      // 0 two bands and one shadow probe, 1 full

const float CT_RADIUS = 6373000.0;   // the shell the domain is laid on (metres)
const float CT_BAND0 = 24000.0;      // band 0's tile: 8 cells of 3 km
const float CT_GRAD = ${ORBIT_GRAD_RANGE.toFixed(1)};
const float CT_RIM = 550.0;          // the narrowest cloud edge, metres
// Band amplitudes, a unit sum of squares: the 3 km band carries 96% of the
// variance, the 1 km band 3% and the 330 m band half a percent: the 1 km
// data already draws the outlines at the finer two's scale, and more of them
// printed grain. Checked against VIIRS trade cumulus at 250 m: the finest
// octave holds 0.05 of the energy, VIIRS 0.04.
const vec3 CT_AMP = vec3(0.9820, 0.1800, 0.0700);
// A band is read once its cells span this many pixels, and fully from the
// second: a band finer than that is an unresolved part of the field.
const vec2 CT_CELL_PX = vec2(1.5, 3.0);
// The finer two bands only from cells of two and a half pixels, fully from
// five: under that they printed an even speckle over every field at 150 to
// 400 km. Unresolved, they widen the carve's edge instead (their mean).
const vec2 CT_CELL_PX_FINE = vec2(2.5, 5.0);
// How strongly a deck's brightness follows the map's departure from its
// twenty-kilometre mean (cloudTopField). Set against VIIRS closed cells off
// Peru at 250 km: the deck's fifth percentile at 0.92 of its median against
// VIIRS's 0.91 (the flat sheet it replaced sat at 0.98).
// With the deck's brightness the data's own (cloudMapSplit's shade), 1.5
// (was 3.5, set when a power of 0.3 flattened it): more drew the 1 km data's
// cells as a crisp crackle over every deck at 1 to 3 km a pixel.
const float CT_DECK_CELLS = 1.5;
// Each band's own rotation of the domain, oblique to the spin axis and to
// one another, so no two share a lattice and none lays a lattice vector
// along the meridians (a rotation about the axis repeated band 1 every
// 8 km north-south round the whole equator).
const mat3 CT_ROT0 = mat3(0.36, 0.48, -0.8, -0.8, 0.6, 0.0, 0.48, 0.64, 0.6);
const mat3 CT_ROT1 = mat3(0.62130178, 0.33136095, 0.71005917, 0.71005917, -0.62130178, -0.33136095, 0.33136095, 0.71005917, -0.62130178);
const mat3 CT_ROT2 = mat3(0.62130178, 0.71005917, -0.33136095, 0.33136095, -0.62130178, -0.71005917, -0.71005917, 0.33136095, -0.62130178);
const mat3 CT_ROTC = mat3(0.36, -0.8, 0.48, 0.48, 0.6, 0.64, -0.8, 0.0, 0.6);
// Organisation of scattered cumulus (cloudTopField): a cluster band of 12 km
// cells, drawn out east-west along the trades and the westerlies (cells
// CT_ALONG times longer than wide), carrying CT_CLUSTER of the field's
// amplitude where the cumulus is scattered over clear sea.
const float CT_ALONG = 2.5;
const float CT_CLUSTER = 0.72;
// A stratocumulus deck's closed cells (cloudTopField): a jittered Voronoi on
// the shell, CT_SC_CELL metres between cell centres, rims CT_SC_RIM metres
// to either side of a cell boundary.
const float CT_SC_CELL = 16000.0;
const float CT_SC_RIM = 650.0;


struct CloudMapPoint {
  float m;      // the map's cover at the point (a page's, where one is resident)
  float m8;     // the 8K's own read there
  float mr;     // the least the 8K reads round the point (its 20 km mip and four texels)
  float mm;     // the 8K's regional mean round the point (its 20 km mip)
  float alpha;  // what that cover stands for as opacity, far off
  float shade;  // the cloud's brightness against a fully lit top (a thin deck is grey)
  float f;      // the fraction of the ground under cloud
  float fk;     // the fraction under thick cloud (the cores, a deck's brighter cells)
  float b;      // 0 a smooth translucent film, 1 discrete cells with crisp rims
  float type;   // 0 a layered deck, 1 convective cumulus
  float thick;  // the most opacity a cloud reaches
  float deck;   // 0 scattered cells on clear sea, 1 a deck that covers the ground
  float rimOp;  // opacity of thin cloud
  float coreOp; // opacity of the cores
  vec2 gm;      // the 8K's gradient per metre (east, north): thicker cloud stands taller
  float strat;  // 0 to 1: a layered stratiform deck (covers the ground and is smooth at 20 km)
  float laneMean; // the deck lanes' mean over the 8K taps round the point (cloudTopField)
  float mb;     // the 8K's broad mean round the point (its 150 km mip)
};

struct CloudTop {
  float cover;  // fraction of the pixel under cloud (its mean is the map's f)
  float alpha;  // opacity: rims translucent, cores near opaque (mean: the map's alpha)
  float core;   // fraction under thick cloud
  float shade;  // brightness factor on the cloud's colour
  float top;    // top height over the ground, metres
  float high;   // 0 low cloud, 1 a high sheet or anvil shield
  vec2 slope;   // d top / d (east, north), as the footprint resolves it
  float occ;    // share of the direct sun past the domes sunward (cloudTopSunOcclusion)
};

vec2 cloudTopUv(vec3 N) {
  return vec2(atan(-N.z, N.x) * 0.15915494 + 0.5, asin(clamp(N.y, -1.0, 1.0)) * 0.31830989 + 0.5);
}
vec3 cloudTopEast(vec3 N) { return normalize(vec3(N.z, 0.0, -N.x) + vec3(1e-5)); }
// The point t metres east and north of N along the shell.
vec3 cloudTopOffset(vec3 N, vec2 t) {
  vec3 e = cloudTopEast(N);
  vec3 n = cross(N, e);
  return normalize(N + (e * t.x + n * t.y) * (1.0 / CT_RADIUS));
}

// The unit normal's CDF (Abramowitz and Stegun 7.1.26, error under 2e-7)
// and its inverse (Giles 2010, single precision).
float ctPhi(float x) {
  float a = abs(x) * 0.70710678;
  float t = 1.0 / (1.0 + 0.3275911 * a);
  float e = (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * exp(-a * a);
  return x < 0.0 ? 0.5 * e : 1.0 - 0.5 * e;
}
float ctPdf(float x) { return 0.39894228 * exp(-0.5 * x * x); }
float ctPhiInv(float p) {
  float x = clamp(2.0 * p - 1.0, -0.9998, 0.9998);
  float w = -log((1.0 - x) * (1.0 + x));
  float r;
  if (w < 5.0) {
    w -= 2.5;
    r = 2.81022636e-08;
    r = 3.43273939e-07 + r * w;
    r = -3.5233877e-06 + r * w;
    r = -4.39150654e-06 + r * w;
    r = 0.00021858087 + r * w;
    r = -0.00125372503 + r * w;
    r = -0.00417768164 + r * w;
    r = 0.246640727 + r * w;
    r = 1.50140941 + r * w;
  } else {
    w = sqrt(w) - 3.0;
    r = -0.000200214257;
    r = 0.000100950558 + r * w;
    r = 0.00134934322 + r * w;
    r = -0.00367342844 + r * w;
    r = 0.00573950773 + r * w;
    r = -0.0076224613 + r * w;
    r = 0.00943887047 + r * w;
    r = 1.00167406 + r * w;
    r = 2.83297682 + r * w;
  }
  return 1.41421356 * r * x;
}

// What a band read at a fractional mip keeps: the variance of its value and
// the mean square of its gradient along the shell (two components, per tile
// unit squared). Measured by reading the baked volume the way this chunk
// does, trilinear at scattered points on a slanted slice, at each level;
// between levels both fall off geometrically. The total at the finest level
// is 1: what trilinear interpolation smooths away is counted as unresolved.
float ctTable(vec4 a, vec4 b, int i) {
  return dot(a, vec4(equal(ivec4(i), ivec4(0, 1, 2, 3)))) + dot(b, vec4(equal(ivec4(i), ivec4(4, 5, 6, 7))));
}
float ctMipLog(float lod, vec4 a, vec4 b) {
  float l = clamp(lod, 0.0, 6.999);
  int i = int(l);
  return exp(mix(ctTable(a, b, i), ctTable(a, b, i + 1), l - float(i)));
}
// log of 0.975, 0.883, 0.659, 0.311, 0.0726, 0.0122, 0.0005, 0.00001
float ctVarShare(float lod) { return ctMipLog(lod, vec4(-0.0253, -0.1244, -0.4170, -1.1680), vec4(-2.6228, -4.4063, -7.6009, -11.5129)); }
// log of 1451, 1121, 619, 181, 26, 1.8, 0.1, 0.01
float ctGradSq(float lod) { return ctMipLog(lod, vec4(7.2800, 7.0220, 6.4281, 5.1985), vec4(3.2581, 0.5878, -2.3026, -4.6052)); }

// A band at P (metres): its unit-normal value and its gradient per metre in
// the planet's frame.
vec4 ctBand(vec3 P, mat3 R, float tile, float lod) {
  vec4 t = textureLod(uCloudOrbitVol, (R * P) * (1.0 / tile), lod);
  vec3 g = (t.gba - 0.5) * (CT_GRAD / tile);
  return vec4((t.r - 0.5) * 8.0, g * R);
}

// De-tiling. The volume repeats, and a lattice vector lying near the shell
// would repeat the pattern along the ground. Each read slides through the
// volume along the vertical by a slow smooth height, so a point one tile
// further along reads a slice a cell away: the repeat is gone, and since
// the slide is along the normal the pattern is not sheared (a slope of 0.15
// stretches it by one percent). Three incommensurate waves of about 70 km.
float ctDetile(vec3 P) {
  vec3 c = vec3(dot(P, vec3(0.91, 0.33, -0.25)), dot(P, vec3(-0.38, 0.84, 0.39)), dot(P, vec3(0.17, -0.44, 0.88)));
  vec3 s = sin(fract(c * vec3(1.0 / 71000.0, 1.0 / 64000.0, 1.0 / 83000.0)) * 6.2831853 + vec3(0.0, 1.7, 4.1));
  return (s.x + s.y + s.z) * 1400.0;
}

// A deck's dark lanes from the map's departure from its regional mean (x,
// relative, times CT_DECK_CELLS): the cells' rims are where the deck is a
// fifth thinner than round it. The 1 km pages resolve the closed cells (10
// to 40 km), so thresholding the departure draws their rims crisp where the
// data's own read is a soft blur. 0 in a cell, 1 in a lane.
float ctDeckLane(float x) { return 1.0 - smoothstep(-0.34, -0.14, x); }

// The map's morphology at a point, from its own mips (earthweather.js
// guidedWeather reads it the same way): compact bright cells against a broad
// smooth bank convect, and so do the tropics. 0 a layered deck, 1 cumulus.
float cloudTopType(float medium, float broad, float lat) {
  float tropics = 1.0 - smoothstep(0.20, 0.65, abs(lat));
  float compact = smoothstep(0.015, 0.18, medium - broad);
  return max(tropics * smoothstep(0.18, 0.65, medium), compact * 0.85);
}

// What a texel reading m stands for as opacity once nothing under it is
// resolved: the globe's far view, and the mean every nearer view keeps.
// The map is a cloud brightness with the clear sea at zero. Where the cloud
// is scattered, a texel's brightness is the share of it under cloud, so the
// opacity follows it in proportion, full at 0.8 (set against DSCOVR EPIC
// discs). A deck is different: it covers the ground, and its brightness is
// its own reflectance, which the map's stretch holds low (the southeast
// Pacific stratocumulus reads 0.36 where VIIRS has the deck at 60% of a
// storm core's brightness). So a deck's opacity rises twice as fast, full
// at 0.4, with a grey a little under white (shade: the linear law's share
// of that opacity to the power 0.3), so the sea under it is hidden and the
// deck reads as a sheet, a thin deck a darker one. deck is the regime, from
// the 8K round the point (cloudMapSplit).
//
// How bright a texel's cloud is, against a thick core: measured by quantile
// matching the 8K over the deep ocean of three DSCOVR EPIC discs against
// EPIC's own brightness there (scripts/earth-refs/score-clouds.mjs, docs/
// earth-clouds.md), linear light. It follows the map almost in proportion
// and rises faster toward the brightest cores, full at 0.79 (a tenth under
// the straight fit through the middle, with cloudTopColour's tops a tenth
// brighter, so the median holds and the cores reach EPIC's white): the
// earlier laws reached full brightness at 0.8 for cumulus and 0.4 for decks,
// which drew a third of all cloud as one flat white plateau that EPIC does
// not have (its cloud brightness falls away steadily from thin to thick).
float cloudMapLaw(float m) {
  float x = clamp(m, 0.0, 1.0);
  return min(0.74 * x + 0.83 * x * x * x, 1.0);
}
float cloudMapAlpha(float m, float opacity, float deck) {
  float thick = min(opacity * 1.14, 0.97);
  return thick * mix(cloudMapLaw(m), clamp(m / 0.4, 0.0, 1.0), deck);
}

// Split the texel's opacity into how much ground is under cloud and how much
// under thick core. Scattered cumulus on clear sea are opaque cores with a
// thin rim that reflects about a fifth of what a core does and lets the sea
// through (a two-stream cloud of optical depth 4 against 60), a texel's
// cloud 1.25 times its opacity in area, so a sparse field is a few bright
// puffs on dark sea: cores that thinned with the cover drew it as twice as
// many grey ghosts. Where no texel round the point is under about a fifth,
// it is a deck that
// covers the ground (the 1 km original is 94% cloud over the southeast
// Pacific stratocumulus at a map value of 0.28, against 20 to 45% in the
// trade cumulus at 0.05): a sheet of nearly one opacity, holes only where a
// thin deck breaks, and its texture in brighter and thicker cells.
// f * rimOp + fk * (coreOp - rimOp) is the alpha, so the split keeps the mean:
// f is held inside the range where that has a solution with 0 <= fk <= f.
void ctSplitCover(inout CloudMapPoint c);
void cloudMapSplit(inout CloudMapPoint c, float opacity) {
  c.thick = min(opacity * 1.14, 0.97);
  c.deck = smoothstep(0.15, 0.3, c.mr);
  c.alpha = cloudMapAlpha(c.m, opacity, c.deck);
  // A deck hides the sea at any brightness, and its brightness is the law's
  // (cloudMapLaw): the opacity rises fast, the grey carries the rest. The
  // composite is in linear light (cloudCompositeWeight), so the deck's light
  // is opacity times shade and follows the data; under the old perceptual
  // weight a power of 0.3 here held decks at 0.8 of white whatever the map
  // said, and the brightness variation inside them (the open and closed
  // cells the 1 km pages carry) was flattened to one grey.
  float lin = c.thick * cloudMapLaw(c.m);
  c.shade = mix(1.0, min(lin / max(c.alpha, 1e-5), 1.0), c.deck * step(1e-5, c.alpha));
  // Covering the ground is not enough to be a deck. Convective by type
  // (cloudTopType: bright and tropical) is not one either, or a smooth anvil
  // shield in the ITCZ was cut into stratocumulus cells. A stratocumulus deck is
  // smooth at twenty kilometres (the 8K's high-pass over the southeast
  // Pacific deck 0.022, b about 0.4); a convective mass in the ITCZ covers
  // as much and is twice as textured (0.047, b 0.85 and over). Drawn as a
  // sheet those masses were flat opaque slabs among the cumulus: they keep
  // the deck's opacity law (the far view's) and take the cumulus split, so
  // their structure is opaque cores, thin rims and gaps.
  c.strat = c.deck * (1.0 - smoothstep(0.5, 0.8, c.b)) * (1.0 - smoothstep(0.45, 0.7, c.type));
  ctSplitCover(c);
}

// The cover and core fractions and their opacities for c.alpha (the rest of
// cloudMapSplit, again after the cirrus takes its share: cloudTopField).
void ctSplitCover(inout CloudMapPoint c) {
  // The sheet: at least three quarters opaque, holes where it is thinner.
  float sheet = min(max(c.alpha, 0.75 * c.thick), c.thick);
  c.rimOp = mix(c.thick * 0.18, sheet * 0.85, c.strat);
  c.coreOp = mix(c.thick, min(sheet * 1.12, c.thick), c.strat);
  float f = mix(min(c.alpha * 1.25, 1.0), min(c.alpha / (sheet * 0.85), 1.0), c.strat);
  c.f = clamp(f, c.alpha / c.coreOp, min(c.alpha / max(c.rimOp, 1e-4), 1.0));
  c.fk = clamp((c.alpha - c.rimOp * c.f) / max(c.coreOp - c.rimOp, 1e-4), 0.0, c.f);
}

// The map at a point: m is the cover to carve (earthweather.js
// earthCloudCover's read of the 8K, the cubic once magnified, or a resident
// page's), m8 the 8K's own read, texel one 8K texel in UV. Everything but
// the cover is measured on the 8K, so it does not change as pages arrive.
CloudMapPoint cloudMapPoint(sampler2D map, vec2 uv, vec2 texel, float m, float m8, float lat, float opacity) {
  CloudMapPoint c;
  c.m = m;
  c.m8 = m8;
  c.mr = 0.0;
  c.mm = m8;
  c.type = 0.0;
  c.b = 0.0;
  c.gm = vec2(0.0);
  c.laneMean = 0.0;
  c.mb = textureLod(map, uv, 5.0).r;
  // Clear sky is most of the map: nothing here is needed where it is clear.
  if (max(m, m8) > 0.004) {
    float medium = textureLod(map, uv, 2.0).r;
    c.type = cloudTopType(medium, c.mb, lat);
    float s = 0.0;
    c.mr = medium;
    c.mm = medium;
    vec2 gUv = vec2(0.0);
    float lanes = ctDeckLane(CT_DECK_CELLS * (m8 - medium) / max(medium, 0.1));
    for (int i = 0; i < 4; i++) {
      vec2 o = vec2(i == 0 || i == 2 ? 1.0 : -1.0, i < 2 ? 1.0 : -1.0);
      float tap = textureLod(map, uv + o * texel, 0.0).r;
      c.mr = min(c.mr, tap);
      s += (tap - medium) * (tap - medium);
      gUv += o * tap;
      lanes += ctDeckLane(CT_DECK_CELLS * (tap - medium) / max(medium, 0.1));
    }
    c.laneMean = lanes * 0.2;
    // The same four taps as a gradient over two texels, in metres east and
    // north: the cloud's mass relief (cloudTopField).
    c.gm = gUv / (4.0 * texel) / vec2(6.2831853 * CT_RADIUS * max(cos(lat), 0.02), 3.1415927 * CT_RADIUS);
    // The residual the texel hides, relative to the most a texel of this
    // mean can hide (all or nothing): cumulus and cellular decks sit near
    // 0.15, frontal shields and stratus near 0.07.
    float q = sqrt(s * 0.25) / sqrt(m8 * (1.0 - m8) + 0.01);
    c.b = mix(0.35, 0.97, smoothstep(0.03, 0.14, q));
  }
  cloudMapSplit(c, opacity);
  return c;
}

// Distance from X (in cells) to the nearest cell boundary of a jittered
// Voronoi (to the bisector plane of the two nearest centres), and the
// vector from X to the nearest centre. 3D, so a slice of it on the shell has
// no seams or poles.
vec3 ctHash33(vec3 p) {
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.xxy + p.yxx) * p.zyx);
}
vec4 ctVoronoi(vec3 X) {
  vec3 i = floor(X);
  vec3 f = X - i;
  float d1 = 1e9, d2 = 1e9;
  vec3 r1 = vec3(0.0), r2 = vec3(1.0);
  for (int a = -1; a <= 1; a++) {
    for (int b = -1; b <= 1; b++) {
      for (int k = -1; k <= 1; k++) {
        vec3 g = vec3(float(a), float(b), float(k));
        vec3 r = g + 0.1 + 0.8 * ctHash33(i + g) - f;
        float d = dot(r, r);
        if (d < d1) { d2 = d1; r2 = r1; d1 = d; r1 = r; }
        else if (d < d2) { d2 = d; r2 = r; }
      }
    }
  }
  vec3 n = r2 - r1;
  return vec4(dot(0.5 * (r1 + r2), n) / max(length(n), 1e-5), r1);
}

// How far a cloud's top rises over a full step of the map (0 to 1): towers
// and convective masses by kilometres, layered sheets and decks by a few
// hundred metres.
float ctMassDepth(CloudMapPoint c) { return mix(mix(500.0, 4000.0, c.type), 300.0, c.deck); }

// The low cloud's carve (cloudTopField without the cirrus).
CloudTop ctLowField(vec3 N, CloudMapPoint c, float pixM, int bands) {
  CloudTop o;
  o.cover = c.f;
  o.alpha = c.alpha;
  o.core = c.fk;
  o.shade = c.shade;
  o.slope = vec2(0.0);
  o.occ = 1.0;
  // High sheets and anvils: bright texels with no texture, and the
  // brightest convective air.
  o.high = clamp(max((1.0 - c.b) * smoothstep(0.3, 0.7, c.m), c.type * smoothstep(0.55, 0.9, c.m)), 0.0, 1.0);
  // Low cloud tops at 1.2 to 1.8 km over domes of 150 m to 1.4 km; high
  // sheets and anvil shields at 9 km, rolling by a few hundred metres.
  float base = mix(1200.0 + 600.0 * c.type, 9000.0, o.high);
  float depth = mix(mix(300.0, 1400.0, c.type) * (0.5 + 0.5 * c.b), 700.0, o.high);
  o.top = base;
  if (c.alpha < 1e-4) return o;

  // The carve's own softness, a function of the place only: how filmy the
  // cloud is (the map's texture; b is the share of an all-or-nothing
  // field's spread the cover keeps, and for a normal latent field the
  // softness that keeps it is this closed form). A deck's holes are soft:
  // its structure is thickness, which the cores below carry. The threshold
  // for the fraction f follows from it and from nothing the view sets.
  float bb = clamp(min(c.b, mix(0.985, 0.8, c.strat)), 0.2, 0.985);
  float W = 1.0 / sin(1.5707963 * bb * bb) - 1.0;
  float t = -sqrt(W + 1.0) * ctPhiInv(c.f);
  float Wk = W + mix(0.4, 0.8, c.strat);
  float tk = -sqrt(Wk + 1.0) * ctPhiInv(max(c.fk, 1e-4));

  vec3 P = N * CT_RADIUS;
  P += N * ctDetile(P);
  vec3 east = cloudTopEast(N);
  vec3 north = cross(N, east);
  float pix = max(pixM, 1.0);
  float zRes = 0.0;       // the resolved part of the latent field
  float vRes = 0.0;       // its variance
  float g2 = 0.0;         // its gradient's mean square, per metre squared
  vec3 grad = vec3(0.0);  // its gradient where a band's cells span pixels
  float zS = 0.0;         // the 3 km band read smooth, for the domes' shape
  float vS = 0.0;         // its variance
  float zF = 0.0;         // the 1 km band where resolved, for a mass's cauliflower
  bool on = uCloudVolOn > 0.5;
  // Organisation. Trade cumulus over open sea is not an even scatter: it
  // gathers in clusters and streets tens of kilometres long with clear sea
  // between, big merged puffs at a cluster's heart and lone specks at its
  // fringe (ISS and Starship photography). Where the cumulus is scattered
  // (no deck, a low regional cover) a cluster band of 12 km cells drawn out
  // east-west joins the sum. The amplitudes keep a unit sum of squares at
  // every point, so the field is a unit normal everywhere and the cover's
  // mean is the map's f as before; once the cluster cells fall under a
  // pixel and a half they are unresolved and the far view is exactly as it
  // was. (Shifting the share toward the 1 km band for regions of specks
  // softened every edge into haze wherever that band was unresolved.)
  // Where the map has structure of its own between its 1 km and 20 km reads
  // (the 1 km pages resolve real clusters and clear gaps), the data is the
  // truth and the cluster band fades out; it only supplies what a flat map
  // cannot say.
  float mapFlat = 1.0 - smoothstep(0.02, 0.08, abs(c.m - c.mm));
  float org = (1.0 - c.deck) * (1.0 - smoothstep(0.25, 0.55, c.mm));
  float aC = CT_CLUSTER * org * mix(0.6, 1.0, mapFlat);
  vec3 amp = CT_AMP * sqrt(1.0 - aC * aC);
  if (on && bands > 0 && aC > 0.0) {
    float tile = CT_BAND0 * 4.0;
    float wgt = smoothstep(CT_CELL_PX.x, CT_CELL_PX.y, tile * 0.125 / (CT_ALONG * pix));
    if (wgt > 0.0) {
      // The mip for the footprint along the cells' length: read at the
      // narrow axis's mip the band lost a quarter of its variance to the
      // filter at a 1 km pixel, and that unresolved share softened every
      // puff in the cluster; the narrow axis is guarded by wgt instead.
      float lod = log2(pix * (128.0 / tile));
      // Its own slide through the volume, five times slower and seven
      // times deeper than ctDetile, or its 96 km tile would repeat.
      vec3 PC = P + N * (ctDetile(P * 0.2) * 7.0);
      vec4 bnd = ctBand(PC * vec3(1.0, CT_ALONG, 1.0), CT_ROTC, tile, max(lod, 0.0));
      zRes += aC * wgt * bnd.x;
      vRes += ctVarShare(lod) * aC * aC * wgt * wgt;
      g2 += aC * aC * wgt * wgt * ctGradSq(lod) * (1.0 + CT_ALONG * CT_ALONG) * 0.5 / (tile * tile);
    }
  }
  for (int k = 0; k < 3; k++) {
    if (k >= bands || !on) break;
    float tile = CT_BAND0 / (k == 0 ? 1.0 : (k == 1 ? 3.0 : 9.0));
    vec2 cellPx = k == 0 ? CT_CELL_PX : CT_CELL_PX_FINE;
    float wgt = smoothstep(cellPx.x, cellPx.y, tile * 0.125 / pix);
    if (wgt <= 0.0) continue;
    float ak = k == 0 ? amp.x : (k == 1 ? amp.y : amp.z);
    float lod = log2(pix * (128.0 / tile));
    vec4 bnd = ctBand(P, k == 0 ? CT_ROT0 : (k == 1 ? CT_ROT1 : CT_ROT2), tile, max(lod, 0.0));
    zRes += ak * wgt * bnd.x;
    vRes += ctVarShare(lod) * ak * ak * wgt * wgt;
    g2 += ak * ak * wgt * wgt * ctGradSq(lod) / (tile * tile);
    // The relief. The slope only takes a band while its cells span four
    // pixels or more, or the shading prints the finest band as pixel noise.
    // The field's cells meet in creases (its Worley part), and the finer
    // bands are as steep as the coarse one for a tenth of its amplitude: lit
    // at the field's own resolution the tops read as dented plaster and
    // crumpled foil. A cloud's top is rounded domes, so the domes are the
    // 3 km band read a mip and a half coarser, its shape and its slope, with
    // a little of the 1 km band's slope for the bumps on their shoulders.
    float rw = ak * wgt * (1.0 - smoothstep(1.5, 3.0, lod));
    if (k == 0 && rw > 0.0) {
      vec4 sm = ctBand(P, CT_ROT0, tile, max(lod, 0.0) + 1.5);
      zS = ak * wgt * sm.x;
      vS = ctVarShare(max(lod, 0.0) + 1.5) * ak * ak * wgt * wgt;
      grad += sm.yzw * rw;
    }
    if (k == 1) {
      grad += bnd.yzw * rw * 0.3 * (1.0 - 0.6 * c.strat);
      zF = ak * wgt * bnd.x;
    }
  }
  // The resolved field still varies across the pixel (and across the
  // narrowest edge a cloud has): that spread is moved from the resolved
  // share to the unresolved, the resolved value shrunk to match, so the
  // total, and with it the mean, is unchanged.
  float spread = g2 * max(CT_RIM, pix) * max(CT_RIM, pix) * 0.25;
  float vR = max(vRes - spread, 0.0);
  float zR = zRes * sqrt(vR / max(vRes, 1e-6));
  float sigU2 = 1.0 - vR;
  float S = sqrt(W + sigU2);
  float Sk = sqrt(Wk + sigU2);
  // Cumulus: the field above the threshold, so each cloud is a bump of it.
  // Cores: the same field above a higher threshold, a little softer, so the
  // thickness grows inward from the rim.
  float coverC = ctPhi((zR - t) / S);
  float coreC = ctPhi((zR - tk) / Sk);
  // A deck is closed cells: domes packed edge to edge, thin and dark only in
  // the narrow lanes between them. The lanes are the field's zero crossings,
  // a connected network, so a deck is cut on the field's size |z| (the
  // regions far from zero are the cells) rather than on z, whose low side
  // printed the holes as round dark spots. |z| over a threshold a is two
  // tails of the same normal, so the cover stays closed form: aD is set so
  // the two tails hold f, and the mean is the map's as before.
  float aD = -sqrt(W + 1.0) * ctPhiInv(0.5 * c.f);
  float akD = -sqrt(Wk + 1.0) * ctPhiInv(0.5 * max(c.fk, 1e-4));
  float coverD = ctPhi((zR - aD) / S) + ctPhi((-zR - aD) / S);
  float coreD = ctPhi((zR - akD) / Sk) + ctPhi((-zR - akD) / Sk);
  // Only a layered deck is cells, and only where it covers the ground: a
  // convective mass keeps its towers, and where a deck breaks up toward its
  // edge (f under 0.95) the cloud is cumulus again, its rim the cumulus
  // cut's crisp lumps rather than the cells' widening lanes.
  float fold = c.strat * smoothstep(0.8, 0.95, c.f);
  float cover = mix(coverC, coverD, fold);
  float core = min(mix(coreC, coreD, fold), cover) * step(1e-4, c.fk);
  o.cover = cover;
  o.core = core;
  o.alpha = c.rimOp * cover + (c.coreOp - c.rimOp) * core;
  // A deck's cells are brighter as well as thicker: the brightness follows
  // the core share of the cover against its mean, so the mean is kept.
  // A deck's: the carve's 3 km cores printed a fine mottle over the sheet,
  // so a stratiform deck takes a third of it.
  o.shade = c.shade * (1.0 + 0.35 * c.deck * (1.0 - 0.7 * c.strat) * (core / max(cover, 1e-3) - c.fk / max(c.f, 1e-3)));
  // A deck's cells are the data's own: where the sheet is opaque its
  // brightness is what the map varies in (the 1 km pages resolve closed and
  // open cells, 8% of the mean at a kilometre over the southeast Pacific),
  // so the brightness follows the map's departure from its twenty-kilometre
  // mean, which averages to nothing over a region and keeps the far view's
  // mean. Past a 20 km pixel there is nothing under the mean to show.
  // A stratiform deck shows them as cells: bright, a little domed, their
  // rims thin and darker (ctDeckLane on the same departure, its mean over
  // the 8K's taps taken off, so the brightness keeps its mean), where the
  // linear law drew soft grey blotches. Convective masses keep that law.
  float cells = c.deck * (1.0 - smoothstep(5000.0, 20000.0, pix));
  float xd = CT_DECK_CELLS * (c.m - c.mm) / max(c.mm, 0.1);
  float laneD = ctDeckLane(xd);
  float deckCells = clamp(0.2 * xd, -0.07, 0.07) - 0.05 * (laneD - c.laneMean);
  o.shade *= 1.0 + cells * mix(clamp(xd, -0.45, 0.45), deckCells, c.strat);
  // A convective mass covers its texel and its cover cannot show its
  // structure: its towers are brighter and the gaps between them darker,
  // the smooth 3 km field and the 1 km band where resolved (zero mean, so
  // the far view is the map's). Fades with the field to the mean.
  // A smooth mass (low b) is an anvil shield: smooth on top.
  float massTex = (1.0 - c.strat) * smoothstep(0.6, 0.95, cover) * smoothstep(0.2, 0.8, c.type + c.deck) * smoothstep(0.45, 0.8, c.b);
  o.shade *= 1.0 + 0.26 * massTex * clamp(zS + zF, -2.5, 2.5);
  // Heights: each cumulus a dome rising from its rim; a deck's cells domes
  // rising from the lanes between them (the field's size, smooth through
  // zero, less its mean so the mean height is the far view's), or a sheet
  // that covers everything would be flat.
  float Sd = sqrt(W + sigU2 + 0.25);
  float zA = sqrt(zS * zS + 0.04);
  float x = mix((zS - t) / Sd - 0.8, (zA - 0.7979 * sqrt(vS)) / Sd, fold);
  // Inside a mass the cumulus dome's shape saturates (the whole texel is
  // cloud), and a convective top is turrets all over: the smooth field
  // itself lifts and lowers it, by hundreds of metres in towers. Zero mean,
  // so the mean height is the far view's.
  float turret = mix(80.0, 190.0, c.type) * (1.0 - fold);
  o.top = base + depth * ctPhi(x) + turret * zS;
  // A deck's relief is its cells' gentle rolls: the carve's 3 km cells at a
  // cumulus dome's slope printed a cotton-wool pattern over the sheet.
  vec2 gT = vec2(dot(grad, east), dot(grad, north)) * mix(1.0, zS / zA, fold);
  // Cumulus domes are steep, cauliflower-sided: the shading takes a quarter
  // again the carve's own slope. A deck's cells are gentle.
  o.slope = gT * ((depth * ctPdf(x) / Sd) * mix(1.25, 0.25, fold) + turret);
  // A stratocumulus deck's closed cells, where they span pixels: each a low
  // lumpy dome, brightest over its centre and dimming toward the gap where
  // it meets its neighbours, the gap itself a little darker still. The cells
  // are warped by the smooth 3 km field's own gradient, so no boundary is
  // straight (straight Voronoi edges read as cracked mud). Each falloff's
  // mean is taken off (w (2.75 - 2.6 w) for a half-width of w cells, measured
  // on this Voronoi), so the brightness keeps its mean; a gap narrower than a
  // pixel widens to it and fades by the same share, so it never prints a
  // pixel-wide line.
  float scW = fold * (1.0 - smoothstep(0.12, 0.3, pix / CT_SC_CELL));
  if (scW > 0.0) {
    vec3 X = P * (1.0 / CT_SC_CELL) + grad * 45.0;
    vec4 v = ctVoronoi(X);
    float wB = 0.3;
    float dome = 1.0 - smoothstep(0.0, wB, v.x);
    float rim = max(CT_SC_RIM, pix);
    float w = min(rim / CT_SC_CELL, 0.3);
    float gap = 1.0 - smoothstep(0.0, w, v.x);
    // Half the old relief (0.17 and 0.04): the data's own cells now show
    // through the deck's brightness, and the Voronoi over them read as a
    // second, regular pattern.
    float gapAmp = 0.02 * (CT_SC_RIM / rim);
    o.shade *= 1.0 - scW * (0.08 * (dome - wB * (2.75 - 2.6 * wB)) + gapAmp * (gap - w * (2.75 - 2.6 * w)));
    // The dome: 150 m over a cell, its slope toward the cell's centre (v.yzw,
    // the nearest centre from X in cells), in the tangent frame per metre.
    vec2 toC = vec2(dot(v.yzw, east), dot(v.yzw, north));
    o.slope += scW * toC * (2.0 * 150.0 / (0.25 * CT_SC_CELL));
  }
  // The mass under the texel: thicker cloud in the data stands taller, a
  // convective mass by kilometres over its tens of kilometres, a deck by a
  // few hundred metres. The 8K's own gradient, so it is the data's relief,
  // faded out before the texel falls under a pixel (at that range it could
  // only alias).
  o.slope += c.gm * ctMassDepth(c) * (1.0 - smoothstep(1500.0, 4000.0, pix));
  float sl = length(o.slope);
  if (sl > 1.6) o.slope *= 1.6 / sl;
  return o;
}

// Cirrus. Thin ice cloud at nine to ten kilometres, in streaks and fibres
// drawn out along the wind, the sea and the cumulus seen through it. The map
// has no heights and cannot tell it from low cloud, so the cirrus is a share
// of the map's own opacity: in regions where the broad map has cloud, a mean
// opacity mu (CI_MEAN, under 0.7 of the region's) is taken out of the low
// cloud in proportion (each texel's low opacity times (1 - mu / A) / (1 - mu),
// A the region's mean) and laid over everything as the cirrus, so the
// region's mean opacity is the map's and the far view is unchanged, and the
// streaks cross clear sea between the cumulus as they do in ISS and Starship
// frames. The streaks are a unit-normal field cut at the cover mu / CI_OPACITY
// like the low cloud's, from three bands of the orbit volume read squashed
// across the wind (48 km cells ten times and 24 km cells five times:
// veils five kilometres wide and 48 long, broken along their length; a
// third, fine band for fibres inside the veils drew hairline scratches and
// is left out), cut at a wide cover of
// low opacity (thin veils over a third of the region, not opaque dashes),
// each band read along two fixed axes and the two
// blended by a slow field so the streaks turn from place to place. A
// squash along a fixed axis is one linear map of the planet's frame, the
// same everywhere, so the pattern is place-anchored; read at the mip of the
// squashed axis, the blur along the streak is the same in the field's own
// frame, so nothing is lost. Unresolved, it is the mean veil mu.
const float CI_MEAN = 0.07;
// 0.1 (was 0.2): set under the old perceptual composite, where an opacity of
// 0.2 over dark sea showed as about 0.07 of a thick cloud's light. In linear
// light (cloudComposite) 0.2 is 0.2, and the streaks read as brush strokes
// across every view from 400 to 1000 km; the mean veil CI_MEAN is unchanged.
const float CI_OPACITY = 0.1;
const vec2 CI_AMP = vec2(0.94, 0.34);
const vec2 CI_SQUASH = vec2(10.0, 5.0);
const vec2 CI_TILE = vec2(384000.0, 192000.0);
const vec3 CI_AXIS1 = vec3(0.0, 1.0, 0.0);
const vec3 CI_AXIS2 = vec3(0.70710678, 0.70710678, 0.0);

// The region's cirrus opacity at N for the map point c.
float ctCirrusMean(vec3 N, CloudMapPoint c) {
  if (uCloudVolOn < 0.5) return 0.0;
  vec3 P = N * CT_RADIUS;
  // Patches and bands of cirrus some hundreds of kilometres across.
  vec3 a = sin(fract(P * vec3(1.0 / 230000.0, 1.0 / 170000.0, 1.0 / 260000.0)) * 6.2831853 + vec3(2.1, 0.7, 4.4));
  vec3 b = sin(fract(P * vec3(1.0 / 97000.0, 1.0 / 113000.0, 1.0 / 89000.0)) * 6.2831853 + vec3(5.3, 3.1, 1.2));
  float patchW = smoothstep(-0.7, 0.5, (a.x + a.y + a.z) * 0.45 + (b.x + b.y + b.z) * 0.2);
  float region = smoothstep(0.02, 0.08, c.mb) * (1.0 - c.strat);
  float A = c.thick * clamp(c.mm / mix(0.8, 0.4, c.deck), 0.0, 1.0);
  return min(CI_MEAN * c.thick * region * patchW, 0.7 * A);
}

// A squashed band read: z at P squashed s times along axis u.
float ctCirrusRead(vec3 P, vec3 u, float s, float tile, float lod) {
  vec3 Q = P + u * (dot(P, u) * (s - 1.0));
  return ctBand(Q, CT_ROT1, tile, lod).x;
}

// The cirrus opacity over a pixel of pixM metres at N, mean mu.
float ctCirrusAlpha(vec3 N, CloudMapPoint c, float pixM, float mu) {
  if (mu <= 1e-4) return 0.0;
  float op = CI_OPACITY * c.thick;
  float f = min(mu / op, 1.0);
  vec3 P = N * CT_RADIUS;
  P += N * (ctDetile(P * 0.3) * 4.0);
  float pix = max(pixM, 1.0);
  // The streaks' heading: the two axes blended by a slow field.
  vec3 hs = sin(fract(P * vec3(1.0 / 410000.0, 1.0 / 330000.0, 1.0 / 370000.0)) * 6.2831853 + vec3(0.3, 2.6, 5.0));
  // One heading per place, switching over a narrow band: blended half and
  // half everywhere, the two headings drew a cross-hatch.
  float th = 1.5707963 * smoothstep(-0.25, 0.25, (hs.x + hs.y + hs.z) * 0.4);
  float c1 = cos(th), s1 = sin(th);
  float zRes = 0.0, vRes = 0.0, g2 = 0.0;
  for (int k = 0; k < 2; k++) {
    float tile = k == 0 ? CI_TILE.x : CI_TILE.y;
    float sq = k == 0 ? CI_SQUASH.x : CI_SQUASH.y;
    float ak = k == 0 ? CI_AMP.x : CI_AMP.y;
    // Resolved by the streak's width: its cells squashed sq times.
    float wgt = smoothstep(CT_CELL_PX.x, CT_CELL_PX.y, tile * 0.125 / (sq * pix));
    if (wgt <= 0.0) continue;
    float lod = log2(sq * pix * (128.0 / tile));
    float z = c1 * ctCirrusRead(P, CI_AXIS1, sq, tile, max(lod, 0.0)) + s1 * ctCirrusRead(P, CI_AXIS2, sq, tile, max(lod, 0.0));
    zRes += ak * wgt * z;
    vRes += ctVarShare(lod) * ak * ak * wgt * wgt;
    g2 += ak * ak * wgt * wgt * ctGradSq(lod) * (1.0 + sq * sq) * 0.5 / (tile * tile);
  }
  float spread = g2 * max(CT_RIM, pix) * max(CT_RIM, pix) * 0.25;
  float vR = max(vRes - spread, 0.0);
  float zR = zRes * sqrt(vR / max(vRes, 1e-6));
  const float W = 1.1;
  float t = -sqrt(W + 1.0) * ctPhiInv(f);
  float cover = ctPhi((zR - t) / sqrt(W + 1.0 - vR));
  return clamp(op * cover, 0.0, 0.97);
}

// The carve at N (drifted address) for the map point c, for a pixel spanning
// pixM metres; bands is how many of the three to read at most. With the
// volume unbound or the footprint past every band it is the map's own mean.
// With all three bands (the globe, the shell and the volume's tops at full
// quality) the cirrus takes its share and is laid over the low cloud: one
// colour for the two layers, the light weighted by each one's share of the
// opacity (flat-lit and high for the cirrus). The probes read fewer bands
// and see the low cloud alone.
CloudTop cloudTopField(vec3 N, CloudMapPoint c, float pixM, int bands) {
  float mu = bands >= 3 ? ctCirrusMean(N, c) : 0.0;
  if (mu > 1e-4) {
    float A = c.thick * clamp(c.mm / mix(0.8, 0.4, c.deck), 0.0, 1.0);
    c.alpha *= (1.0 - mu / max(A, 1e-4)) / (1.0 - mu);
    ctSplitCover(c);
  }
  CloudTop o = ctLowField(N, c, pixM, bands);
  if (mu > 1e-4) {
    float aci = ctCirrusAlpha(N, c, pixM, mu);
    float total = 1.0 - (1.0 - o.alpha) * (1.0 - aci);
    float w = aci / max(total, 1e-4);
    o.alpha = total;
    o.slope *= 1.0 - w;
    o.occ = mix(o.occ, 1.0, w);
    o.top = mix(o.top, 9500.0, w);
    o.shade = mix(o.shade, 1.0, w);
    o.high = max(o.high, w);
  }
  return o;
}

// The ground under a pixel in the map's drifted address. The cloud read point
// N and the ground point Ng are a few kilometres apart along the view ray,
// and the weather's warp and drift barely change over that, so the ground
// takes the cloud point's address moved by the same offset (Nd is N's).
vec3 cloudTopGroundAddress(vec3 Nd, vec3 N, vec3 Ng) { return normalize(Nd + (Ng - N)); }

// The face of a cloud mass away from the sun reads darker: the 8K a hundred
// kilometres sunward against the 8K here (sT the sun in the tangent frame,
// lod the map level for the pixel). Both sides from the 8K, so the term is a
// hundred-kilometre mass whether a page is resident or not, and a function
// of place, the same at every distance. Returns the factor on the colour.
float cloudMassShade(sampler2D map, vec2 uv, CloudMapPoint c, vec3 sT, float lod) {
  if (c.m8 < 0.05 || sT.z <= 0.0) return 1.0;
  float sunside = textureLod(map, uv + sT.xy * 0.0035, lod).r;
  return 1.0 - clamp(sunside - c.m8, 0.0, 0.5) * smoothstep(0.05, 0.4, c.m8) * 0.45 * sT.z;
}

// Self-shadowing: the share of the direct sun that reaches the top at N past
// the domes up the sun's azimuth (N the drifted address, c its map point, ct
// its carve, sT the sun in the tangent frame). Two probes along the sun, at
// a third and at the whole of the length a dome throws a shadow at this
// elevation, each the carve there at the coarse bands: a neighbour taller
// than the sun's line by then shades this point, as much as it is opaque.
// A function of place only, faded in with the slope's own band weight, so
// it is there only where the domes are resolved and the far view is the
// unshaded mean exactly. At a high sun the line clears every dome; toward
// the terminator whole flanks fall into their neighbours' shadow.
float cloudTopSunOcclusion(vec3 N, CloudMapPoint c, CloudTop ct, float pixM, vec3 sT) {
  if (sT.z < 0.02 || ct.cover < 0.02 || uCloudVolOn < 0.5) return 1.0;
  float w = 1.0 - smoothstep(1.5, 3.0, log2(max(pixM, 1.0) * (128.0 / CT_BAND0)));
  if (w <= 0.0) return 1.0;
  float horiz = max(length(sT.xy), 1e-4);
  vec2 h = sT.xy / horiz;
  float tanE = max(sT.z / horiz, 0.08);
  // The length a kilometre of relief throws, held between half a cell and
  // two cells of the 3 km band.
  float L = clamp(900.0 / tanE, 1500.0, 6000.0);
  float wMass = 1.0 - smoothstep(1500.0, 4000.0, max(pixM, 1.0));
  float occ = 1.0;
  int probes = uCloudTopQuality > 0.5 ? 2 : 1;
  for (int i = 0; i < 2; i++) {
    if (i >= probes) break;
    float d = i == 0 ? L : L * 0.35;
    // The 3 km band alone: the domes' shape is all its own (the dome heights
    // read it smooth), and a second band read per probe cost a third again.
    CloudTop p = cloudTopField(cloudTopOffset(N, h * d), c, pixM, 1);
    // The mass relief carried on by the map's gradient along the probe.
    float rise = p.top - ct.top + dot(c.gm, h) * d * ctMassDepth(c) * wMass - d * tanE;
    // A turret's shadow is not black: its own edges let some sun through.
    occ = min(occ, 1.0 - 0.75 * min(p.cover * 0.6 + p.core * 0.4, 1.0) * smoothstep(-250.0, 500.0, rise));
  }
  // No renormalisation: dividing by Smith's lit fraction for the domes' rms
  // slope overshot the probes' own shadowing four times over (frames 4 to 5%
  // brighter); as it is, a 12 degree sun over convective tops at 400 km
  // reads 1.4% darker than the unshaded mean, a deck 0.3%, and past the
  // 3 km band's resolution w is 0 and the far view is exact.
  return mix(1.0, occ, w);
}

// How much of the light off the ground a cloud's shadow takes, per channel.
// Land is mostly the direct sun it reflects. The sea is two lights: sky
// reflected off its surface (EO_SKY, earthocean.js), which a cloud barely
// shades, and the water column lit by the sun and the sky, whose direct
// share (0.75 of the globe's lighting) the shadow takes. Deep clear water
// at nadir (EO_REFERENCE) is (0.11, 0.42, 0.76) column by channel, so the
// shadow takes (0.08, 0.32, 0.57): the shaded sea goes darker and greyer,
// the blue going out of it. The air light over it is the veil's, shaded
// apart (cloudTopVeilShadow).
vec3 cloudTopShadowStrength(float ocean) { return mix(vec3(0.55), vec3(0.08, 0.32, 0.57), clamp(ocean, 0.0, 1.0)); }

// The share of the veil (the air light over the ground) that a cloud's
// shadow takes: the air below the cloud base, most of the marine haze and a
// tenth of the Rayleigh column, about 0.3 of the path light looking down. A
// slant ray crosses that layer over a long stretch of sun and shade alike,
// so the share falls with the view's air mass (mu the view's cosine).
float cloudTopVeilShadow(float mu) { return 0.3 * clamp(mu * 1.25, 0.0, 1.0); }

// The cloud's own colour: a white top lit by the sun over its slope and by
// the blue sky, warmed through the terminator, then veiled by the air above
// it (less of it the higher the top: a tower's anvil reads crisper than the
// cumulus round it). sT the sun in the tangent frame, veil and haze the
// caller's aerial-perspective colour and strength for the ground.
//
// Relief: facing is how much more of the sun a face takes than a flat top
// (its slope against the sun's azimuth, by how low the sun is). The sun's
// light is the flux on the face per unit of ground, sT.z + facing, and the
// light diffused through the cloud from its sunlit side follows it too, so
// a flank turned from the sun is lit by the blue sky alone. Both are linear
// in the slope, so over a field of domes the mean is the flat top's, and a
// flat top (the far view, where no slope is resolved) is exactly the old
// colour. At a high sun facing is small and the domes read flat white, as
// in Mercury and Gemini photography; toward the terminator the flanks part
// three to six to one, as from the ISS. ct.occ is the domes sunward
// (cloudTopSunOcclusion).
vec3 cloudTopColour(CloudTop ct, vec3 sT, vec3 veil, float haze) {
  float facing = -dot(ct.slope, sT.xy);
  float direct = max(sT.z + facing, 0.0) * ct.occ;
  // Half the fill is the sky's (blue), half the sun's own light diffused
  // through the cloud from its lit side (white), which follows the facing;
  // flat and unshaded they sum to the old fill, (0.92, 0.96, 1.02) * 0.40.
  // In a neighbour's shadow the cloud round it still diffuses light in, so
  // a shaded hollow stays a mid blue-grey, lighter than the sea.
  vec3 amb = vec3(0.16, 0.18, 0.216) + vec3(0.208, 0.204, 0.192) * max(1.0 + 1.8 * facing, 0.0) * mix(0.55, 1.0, min(ct.occ, 1.0));
  // The sun's share 0.70 (was 0.62): EPIC's thick cores sit at 0.9 of their
  // white against the render's 0.8, with the clear sea matched; the
  // shoulder below still holds them under the bloom.
  vec3 col = (amb + vec3(direct * 0.70)) * ct.shade;
  float term = smoothstep(0.22, 0.04, sT.z) * smoothstep(-0.22, 0.0, sT.z);
  if (term > 0.002) col = oklchMixNeutral(col, col + vec3(1.0, 0.86, 0.70) * 0.24 * ct.shade * min(ct.occ, 1.0), term);
  // The veil over a top is the air above it: the Rayleigh column falls with
  // an 8 km scale height, and the marine aerosol the veil also carries (about
  // 0.4 of it, fitted to the clear sea) lives in the boundary layer under the
  // tops, a 1.5 km scale height. Over the whole column it laid an aerosol
  // veil on the cloud that is not above it, which greyed and blued the tops
  // (EPIC's mid cloud is neutral, the render's bluer by 0.04 in (B-R)/(B+R)).
  float h = haze * (0.6 * exp(-ct.top / 8000.0) + 0.4 * exp(-ct.top / 1500.0));
  if (h > 0.002) col = oklchMixNeutral(col, veil, h);
  return cloudLumaShoulder(col);
}
`;
